import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { api, errMsg, fmtDate, fmtMoney } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { fileToDataUrl } from "../../lib/upload";
import { useAuth } from "../../context/AuthContext";
import { Logo, Wordmark } from "../../components/shared/Logo";
import { ThemeToggle } from "../../components/shared/ThemeToggle";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  LogOut, MapPin, CheckCircle2, Rocket, CalendarDays, Trophy, Wallet, Briefcase, Loader2,
  HandCoins, Navigation, Camera, Copy, Smartphone, ChevronRight, X, Package, Menu, Receipt,
  HelpCircle, User, Mail, Phone,
} from "lucide-react";
import { getPushState, enablePush, disablePush, sendTestPush } from "../../lib/push";
import { LevelBadge } from "../../components/shared/LevelBadge";
import MoveMap from "../../components/shared/MoveMap";

// ---- Lugg-style live-job palette (fixed, screen-local) ----
const LG = { pink: "#EC4899", indigo: "#6C6CF0", indigo2: "#7C7CF7" };

// Drag-to-confirm pill (slide to start / complete)
function SlideAction({ label, onConfirm, resetKey, testId }) {
  const track = useRef(null);
  const [x, setX] = useState(0);
  const [w, setW] = useState(0);
  const drag = useRef(false);
  const moved = useRef(false);
  const KNOB = 60;
  useEffect(() => { setX(0); }, [resetKey]);
  useEffect(() => {
    const measure = () => setW(track.current?.offsetWidth || 0);
    measure(); window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const max = Math.max(0, w - KNOB - 8);
  const move = (clientX) => {
    if (!drag.current || !track.current) return;
    moved.current = true;
    const rect = track.current.getBoundingClientRect();
    setX(Math.max(0, Math.min(clientX - rect.left - KNOB / 2, max)));
  };
  const end = () => {
    if (!drag.current) return;
    drag.current = false;
    if (x >= max - 6) { setX(max); onConfirm?.(); }
    else setX(0);
  };
  const pct = max > 0 ? x / max : 0;
  return (
    <div ref={track} className="relative h-[66px] w-full rounded-full overflow-hidden select-none touch-none"
      style={{ background: LG.indigo }}
      onPointerMove={(e) => move(e.clientX)} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}>
      <span className="absolute inset-0 flex items-center justify-center font-heading font-extrabold text-white text-lg pointer-events-none"
        style={{ opacity: 1 - pct * 1.4 }}>{label}</span>
      <div data-testid={testId} className="absolute top-1 bottom-1 rounded-full bg-white flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing"
        style={{ width: KNOB, transform: `translateX(${x + 4}px)` }}
        onPointerDown={(e) => { drag.current = true; moved.current = false; e.currentTarget.setPointerCapture?.(e.pointerId); }}
        onClick={() => { if (!moved.current) onConfirm?.(); }}>
        <ChevronRight className="w-6 h-6" style={{ color: LG.indigo }} strokeWidth={3} />
      </div>
    </div>
  );
}

function AddressPair({ pickup, dropoff }) {
  return (
    <div className="rounded-2xl bg-[#17181c] p-4">
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center pt-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: LG.indigo, boxShadow: `0 0 0 3px rgba(108,108,240,.2)` }} />
          {dropoff && <span className="w-[2px] flex-1 my-1 rounded" style={{ background: LG.indigo, minHeight: 28 }} />}
          {dropoff && <span className="w-3 h-3 rounded-full border-2" style={{ borderColor: LG.indigo }} />}
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <p className="font-heading font-semibold text-white text-[17px] leading-snug truncate">{pickup || "Pickup"}</p>
          {dropoff && <p className="font-heading font-semibold text-white text-[17px] leading-snug truncate">{dropoff}</p>}
        </div>
      </div>
    </div>
  );
}

function LiveJobScreen({ job, photos = [], onClose, onPatch, onAddPhoto, uploading, me }) {
  const [baseRoute, setBaseRoute] = useState(null);   // pickup -> dropoff (resolved coords + geometry)
  const [activePath, setActivePath] = useState(null); // colored gradient: crew -> current stop
  const [stopIndex, setStopIndex] = useState(0);
  const started = job.status === "in_progress";

  const primaryAddr = job.pickup_address || job.dropoff_address || "";
  const hasDrop = !!(job.dropoff_address && !job.same_place && job.dropoff_address !== job.pickup_address);
  const stops = useMemo(() => {
    const arr = [];
    if (job.pickup_address) arr.push({ type: "Pickup", address: job.pickup_address });
    if (hasDrop) arr.push({ type: "Dropoff", address: job.dropoff_address });
    return arr.length ? arr : [{ type: "Job site", address: primaryAddr || "On site" }];
  }, [job]); // eslint-disable-line
  const current = stops[Math.min(stopIndex, stops.length - 1)];

  // Base route (pickup -> dropoff) → gives resolved stop coords + the grey inter-stop geometry.
  useEffect(() => {
    if (!primaryAddr) return;
    api.post("/geo/route", {
      pickup: { label: primaryAddr },
      dropoff: hasDrop ? { label: job.dropoff_address } : { label: primaryAddr },
    }).then((r) => setBaseRoute(r.data)).catch(() => {});
  }, [job.id]); // eslint-disable-line

  // Lock the page behind the live screen so it can't scroll/peek through.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const stopCoords = useMemo(() => {
    if (!baseRoute?.pickup?.lat) return [];
    const arr = [{ lat: baseRoute.pickup.lat, lng: baseRoute.pickup.lng }];
    if (hasDrop && baseRoute.dropoff?.lat) arr.push({ lat: baseRoute.dropoff.lat, lng: baseRoute.dropoff.lng });
    return arr;
  }, [baseRoute]); // eslint-disable-line
  const currentStopCoord = stopCoords[Math.min(stopIndex, Math.max(0, stopCoords.length - 1))];
  const pins = useMemo(() => stopCoords.map((c, i) => ({ ...c, n: i + 1 })), [stopCoords]);

  // Colored active leg: crew (me) → current stop. Throttled to ~100m of crew movement.
  const meKey = me ? `${me.lat.toFixed(3)},${me.lng.toFixed(3)}` : null;
  useEffect(() => {
    if (!currentStopCoord) return;
    if (!me) { // no GPS fix → colour the whole base route so there's still a trace
      setActivePath(baseRoute?.geometry?.length > 1 ? baseRoute.geometry.map(([a, b]) => ({ lat: a, lng: b })) : stopCoords);
      return;
    }
    let cancelled = false;
    const straight = [{ lat: me.lat, lng: me.lng }, currentStopCoord];
    api.post("/geo/route", { pickup: { lat: me.lat, lng: me.lng }, dropoff: { lat: currentStopCoord.lat, lng: currentStopCoord.lng } })
      .then((r) => { if (!cancelled) setActivePath((r.data.geometry || []).length > 1 ? r.data.geometry.map(([a, b]) => ({ lat: a, lng: b })) : straight); })
      .catch(() => { if (!cancelled) setActivePath(straight); });
    return () => { cancelled = true; };
  }, [meKey, stopIndex, baseRoute]); // eslint-disable-line

  // Grey upcoming leg: current stop → next stop (activates/colours once the current is completed).
  const greyPath = useMemo(() => {
    if (!baseRoute) return null;
    const cur = stopCoords[stopIndex];
    const next = stopCoords[stopIndex + 1];
    if (!cur || !next) return null;
    if (stopIndex === 0 && baseRoute.geometry?.length > 1) return baseRoute.geometry.map(([a, b]) => ({ lat: a, lng: b }));
    return [cur, next];
  }, [baseRoute, stopIndex, stopCoords]);

  const miles = Math.round(baseRoute?.distance_miles ?? job.distance_miles ?? 0);
  const conf = `#${String(job.token || job.mission_number || job.id).replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase()}`;
  const itemText = (job.items || []).map((it) => `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ""}${it.spec ? ` — ${it.spec}` : ""}`).join(", ")
    || (job.line_items || []).map((li) => `${li.label}${li.qty > 1 ? ` ×${li.qty}` : ""}`).join(", ");
  const detail = job.notes || itemText || `${serviceName(job.service_type)} for ${job.customer_name}`;
  const primaryPhoto = (photos || []).find((p) => ["item", "customer"].includes(p.kind)) || (photos || [])[0];
  const photo = primaryPhoto?.photo || primaryPhoto?.data;

  const contact = () => { if (job.customer_phone) window.location.href = `tel:${job.customer_phone}`; else toast.error("No customer phone on this job"); };
  const navTo = () => window.open(`https://maps.google.com/?q=${encodeURIComponent(current.address)}`, "_blank");

  const startJob = () => onPatch(job.id, { status: "in_progress" });
  const completeStop = async () => {
    if (stopIndex < stops.length - 1) setStopIndex((i) => i + 1);
    else { await onPatch(job.id, { status: "completed" }); onClose(); }
  };

  const chip = (text, active) => (
    <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${active ? "bg-white text-black" : "bg-[#1c1d22] text-white/60"}`}>{text}</span>
  );

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black text-white overflow-hidden isolate"
      style={{ height: "100dvh", width: "100vw" }} data-testid="crew-live-job">
      <div className="absolute inset-0 z-0"><MoveMap me={me} pins={pins} activePath={activePath} greyPath={greyPath} /></div>
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-10" />
      <button onClick={onClose} data-testid="crew-live-close"
        className="absolute left-4 z-40 w-12 h-12 rounded-full bg-black/70 backdrop-blur flex items-center justify-center border border-white/15 active:scale-95"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 14px)" }}>
        <X className="w-5 h-5" />
      </button>

      <div className="absolute inset-x-0 bottom-0 z-20 rounded-t-[28px] bg-[#0b0b0d] border-t border-white/10 flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,0.6)]"
        style={{ maxHeight: "60dvh" }} data-testid="crew-live-sheet">
        <div className="pt-2.5 pb-1 flex justify-center shrink-0"><span className="w-11 h-1.5 rounded-full bg-white/20" /></div>
        <div className="overflow-y-auto overscroll-contain px-5 pt-2 pb-4" style={{ WebkitOverflowScrolling: "touch" }}>
          <h1 className="font-heading font-extrabold text-[34px] leading-none tracking-tight">
            {started ? "Current job" : "Up next"}
          </h1>
          <div className="flex flex-wrap gap-2.5 mt-4">
            {chip(current.type, true)}
            {hasDrop && miles > 0 && chip(`${miles} miles`)}
            {chip(`${stops.length} stop${stops.length !== 1 ? "s" : ""}`)}
          </div>

          {started ? (
            <>
              <div className="mt-5 rounded-2xl bg-[#17181c] p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-xl truncate">{current.address}</p>
                  <p className="text-white/50 text-sm">{job.customer_name}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={contact} data-testid="crew-live-contact" className="px-5 py-3 rounded-full bg-black border border-white/20 font-bold text-sm">Contact</button>
                  <button onClick={navTo} data-testid="crew-live-navigate" className="px-5 py-3 rounded-full bg-black border border-white/20 font-bold text-sm">Navigate</button>
                </div>
              </div>

              <p className="text-white/40 text-sm font-semibold mt-6 mb-2">Details</p>
              <div className="rounded-2xl bg-[#17181c] p-4 flex gap-4">
                {photo
                  ? <img src={photo} alt="item" className="w-20 h-20 rounded-2xl object-cover bg-black shrink-0" />
                  : <div className="w-20 h-20 rounded-2xl bg-black flex items-center justify-center shrink-0"><Package className="w-8 h-8 text-white/30" /></div>}
                <p className="text-white/60 text-[15px] leading-relaxed">{detail}</p>
              </div>

              {hasDrop && (<>
                <p className="text-white/40 text-sm font-semibold mt-6 mb-2">Route</p>
                <AddressPair pickup={job.pickup_address} dropoff={job.dropoff_address} />
              </>)}

              <p className="text-center text-white/35 text-sm font-mono-cc mt-6" data-testid="crew-live-confirmation">Confirmation number: {conf}</p>
            </>
          ) : (
            <>
              <div className="mt-5"><AddressPair pickup={primaryAddr} dropoff={hasDrop ? job.dropoff_address : null} /></div>
              <p className="text-white/40 text-sm font-semibold mt-6 mb-2">Details</p>
              <div className="rounded-2xl bg-[#17181c] p-4 flex gap-4">
                {photo
                  ? <img src={photo} alt="item" className="w-20 h-20 rounded-2xl object-cover bg-black shrink-0" />
                  : <div className="w-20 h-20 rounded-2xl bg-black flex items-center justify-center shrink-0"><Package className="w-8 h-8 text-white/30" /></div>}
                <p className="text-white/60 text-[15px] leading-relaxed">{detail}</p>
              </div>
              <div className="flex gap-2 mt-4">
                {["before", "after"].map((kind) => (
                  <label key={kind} data-testid={`crew-photo-${kind}-${job.mission_number}`}
                    className="flex-1 flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold cursor-pointer capitalize">
                    <Camera className="w-4 h-4" /> {uploading === kind ? "Uploading…" : `Add ${kind}`}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { onAddPhoto(job.id, kind, e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                ))}
              </div>
              <p className="text-center text-white/35 text-sm font-mono-cc mt-5" data-testid="crew-live-confirmation">Confirmation number: {conf}</p>
            </>
          )}
        </div>

        <div className="px-5 pt-2 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-white/5 bg-[#0b0b0d]">
          {started
            ? <SlideAction key={`c${stopIndex}`} testId={`crew-complete-button-${job.mission_number}`}
                label={stopIndex < stops.length - 1 ? `Complete Stop ${stopIndex + 1}` : "Complete Job"}
                resetKey={`c${stopIndex}`} onConfirm={completeStop} />
            : <SlideAction key="start" testId={`crew-start-button-${job.mission_number}`}
                label="Slide to start" resetKey="start" onConfirm={startJob} />}
        </div>
      </div>
    </div>,
    document.body
  );
}

const NAV = [
  { id: "work", label: "Work", icon: Rocket },
  { id: "schedule", label: "Schedule", icon: CalendarDays },
  { id: "earnings", label: "Earnings", icon: Wallet },
  { id: "expenses", label: "Expenses", icon: Receipt },
  { id: "account", label: "Account", icon: User },
  { id: "help", label: "Help center", icon: HelpCircle },
];

export default function CrewPortal() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("work");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scheduleView, setScheduleView] = useState("available");
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [liveJob, setLiveJob] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [photosMap, setPhotosMap] = useState({});
  const [uploading, setUploading] = useState(null);
  const [sharing, setSharing] = useState(() => localStorage.getItem("cc-share-loc") !== "off");
  const [pushState, setPushState] = useState("idle");
  const [pushBusy, setPushBusy] = useState(false);
  const lastSent = useRef(0);

  useEffect(() => { getPushState().then(setPushState); }, []);

  const togglePush = async () => {
    if (pushState === "unsupported") { toast.error("This browser doesn't support push notifications"); return; }
    if (pushState === "denied") { toast.error("Notifications are blocked — allow them in your browser/app settings"); return; }
    setPushBusy(true);
    try {
      if (pushState === "enabled") { await disablePush(); toast.success("Job alerts turned off"); }
      else { await enablePush(); toast.success("Job alerts armed — you'll get buzzed when new jobs post"); }
      setPushState(await getPushState());
    } catch (e) {
      setPushState(await getPushState());
      toast.error(e?.message || "Couldn't enable push notifications");
    }
    setPushBusy(false);
  };

  const fireTest = async () => {
    try {
      const sent = await sendTestPush();
      toast.success(sent > 0 ? `Test alert sent to ${sent} device${sent > 1 ? "s" : ""}` : "No devices subscribed yet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const load = () => {
    api.get("/crew/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/crew/board").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/crew/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Background location sharing to the server (throttled).
  useEffect(() => {
    localStorage.setItem("cc-share-loc", sharing ? "on" : "off");
    if (!sharing || !navigator.geolocation) return;
    const send = (pos) => {
      setMyPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      if (Date.now() - lastSent.current < 20000) return;
      lastSent.current = Date.now();
      api.post("/crew/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
    };
    const id = navigator.geolocation.watchPosition(send, () => {}, { enableHighAccuracy: true, maximumAge: 15000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [sharing]);

  // Live "you are here" dot while the live-job screen is open (even if background sharing is off).
  useEffect(() => {
    if (!liveJob || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setMyPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [liveJob?.id]);

  useEffect(() => {
    const id = liveJob?.id;
    if (id && photosMap[id] === undefined) {
      api.get(`/crew/missions/${id}/photos`)
        .then((r) => setPhotosMap((p) => ({ ...p, [id]: r.data })))
        .catch(() => setPhotosMap((p) => ({ ...p, [id]: [] })));
    }
  }, [liveJob?.id]); // eslint-disable-line

  const addPhoto = async (missionId, kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post(`/crew/missions/${missionId}/photos`, { kind, photo: dataUrl });
      setPhotosMap((p) => ({ ...p, [missionId]: [...(p[missionId] || []), data] }));
      toast.success(`${kind === "before" ? "Before" : "After"} photo added`);
    } catch (e) { toast.error(errMsg(e)); }
    setUploading(null);
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const setStatus = async (status) => { await api.patch("/crew/status", { status }); load(); };

  const patchMission = async (id, payload) => {
    try { await api.patch(`/crew/missions/${id}`, payload); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const patchLive = async (id, payload) => {
    setLiveJob((prev) => (prev && prev.id === id ? { ...prev, ...payload } : prev));
    await patchMission(id, payload);
  };

  const claim = async (id) => {
    setClaiming(id);
    try { await api.post(`/crew/board/${id}/claim`); toast.success("Job claimed — it's on your schedule"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setClaiming(null);
  };

  const openSection = (id) => { setSection(id); setMenuOpen(false); };

  const MissionCard = ({ m }) => (
    <button className="w-full text-left rounded-2xl border bg-card p-5 card-lift" data-testid={`crew-mission-${m.mission_number}`}
      onClick={() => setLiveJob(m)}>
      <div className="flex items-center justify-between gap-3" data-testid={`crew-mission-toggle-${m.mission_number}`}>
        <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number} · {m.scheduled_time}</p>
        <StatusBadge status={m.status} />
      </div>
      <p className="font-heading font-semibold mt-1.5">{m.customer_name} — {serviceName(m.service_type)}</p>
      {m.pickup_address && <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--cc-accent)] shrink-0" /> {m.pickup_address}</p>}
      <div className="flex items-center justify-between mt-2.5">
        <p className="text-xs text-muted-foreground">{fmtDate(m.scheduled_date)}</p>
        <span className="flex items-center gap-2">
          {m.payout_per_helper > 0 && <span className="text-xs font-mono-cc font-semibold text-emerald-500">{fmtMoney(m.payout_per_helper)}</span>}
          <span className="flex items-center gap-1 text-xs font-semibold text-[var(--cc-accent)]">Open <ChevronRight className="w-3.5 h-3.5" /></span>
        </span>
      </div>
    </button>
  );

  const BoardCard = ({ m }) => (
    <div className="rounded-xl border bg-card p-5 card-lift" data-testid={`board-job-${m.mission_number}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number}</p>
        <p className="font-heading font-extrabold text-2xl text-emerald-500" data-testid={`board-payout-${m.mission_number}`}>{fmtMoney(m.payout_per_helper)}</p>
      </div>
      <p className="font-heading font-semibold mt-1">{serviceName(m.service_type)}</p>
      <p className="text-xs text-muted-foreground mt-1">{fmtDate(m.scheduled_date)} · arrive {m.scheduled_time} · {m.open_positions} spot{m.open_positions !== 1 ? "s" : ""} left</p>
      {m.pickup_address && <p className="text-xs mt-2 flex items-center gap-1.5 text-muted-foreground"><MapPin className="w-3.5 h-3.5 text-[var(--cc-accent)]" /> {m.pickup_address}</p>}
      <Button data-testid={`board-claim-${m.mission_number}`} disabled={m.already_claimed || claiming === m.id} onClick={() => claim(m.id)}
        className="w-full mt-4 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold disabled:opacity-60">
        {claiming === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : m.already_claimed ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Claimed</> : <><HandCoins className="w-4 h-4 mr-2" /> Claim This Job</>}
      </Button>
    </div>
  );

  const activeSection = NAV.find((n) => n.id === section) || NAV[0];
  const openBoard = board.filter((j) => !j.already_claimed);

  return (
    <div className="min-h-screen bg-background noise-cc">
      <header className="sticky top-0 z-40 glass-cc bg-background/80 border-b">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <button data-testid="crew-menu-button" onClick={() => setMenuOpen(true)} aria-label="Open menu"
              className="w-10 h-10 rounded-full border flex items-center justify-center hover:border-[var(--cc-accent)] transition-colors">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-2" data-testid="crew-logo-link">
              <Logo size={22} />
              <div>
                <Wordmark className="h-[10px]" />
                <p className="text-[9px] font-mono-cc tracking-[0.25em] text-muted-foreground uppercase">Crew</p>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="crew-help-button" onClick={() => setSection("help")} aria-label="Help center"
              className="w-10 h-10 rounded-full border flex items-center justify-center text-muted-foreground hover:border-[var(--cc-accent)] transition-colors">
              <HelpCircle className="w-5 h-5" />
            </button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* ---- Lugg-style slide-in menu ---- */}
      {menuOpen && createPortal(
        <div className="fixed inset-0 z-[85]" data-testid="crew-menu-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[84%] max-w-xs bg-[#0b0b0d] text-white flex flex-col border-r border-white/10 shadow-2xl"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }} data-testid="crew-menu-drawer">
            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-full bg-[var(--cc-accent)]/20 border border-[var(--cc-accent)]/40 flex items-center justify-center font-heading font-extrabold text-[var(--cc-accent)] shrink-0">
                  {(data?.employee?.name || "C").slice(0, 1)}
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-bold truncate">{data?.employee?.name || "Operative"}</p>
                  {data?.employee && <LevelBadge level={data.employee.crew_level} />}
                </div>
              </div>
              <button onClick={() => setMenuOpen(false)} data-testid="crew-menu-close" aria-label="Close menu"
                className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`crew-nav-${n.id}`} onClick={() => openSection(n.id)}
                  className={`w-full flex items-center gap-3.5 rounded-xl px-4 py-3.5 text-left transition-colors ${section === n.id ? "bg-[var(--cc-accent)]/15 text-[var(--cc-accent)]" : "text-white/80 hover:bg-white/5"}`}>
                  <n.icon className="w-5 h-5 shrink-0" />
                  <span className="font-heading font-semibold text-[15px]">{n.label}</span>
                  {n.id === "schedule" && openBoard.length > 0 && (
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-mono-cc bg-[var(--cc-accent)]/20 text-[var(--cc-accent)]">{openBoard.length}</span>
                  )}
                </button>
              ))}
            </nav>
            <div className="p-3 border-t border-white/10" style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
              <button data-testid="crew-logout-button" onClick={handleLogout}
                className="w-full flex items-center gap-3.5 rounded-xl px-4 py-3.5 text-left text-white/70 hover:bg-white/5">
                <LogOut className="w-5 h-5" /> <span className="font-heading font-semibold text-[15px]">Log out</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <main className="relative z-10 max-w-4xl mx-auto px-5 py-8">
        {!data ? (
          <p className="text-sm text-muted-foreground">Loading your work...</p>
        ) : (
          <div className="space-y-8" data-testid="crew-portal">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline-cc">{activeSection.label}</p>
                <h1 className="font-heading font-bold text-3xl tracking-tight mt-1">
                  {section === "work" ? "Your work" : section === "schedule" ? "Your schedule"
                    : section === "earnings" ? "Earnings" : section === "expenses" ? "Expenses"
                    : section === "account" ? "Account" : "Help center"}
                </h1>
                {section === "work" && <p className="text-sm text-muted-foreground mt-1">{data.employee.name} · {data.employee.role}</p>}
              </div>
              {earnings && (section === "work" || section === "earnings") && (
                <div className="text-right">
                  <p className="overline-cc">Pending Earnings</p>
                  <p className="font-heading font-bold text-2xl text-emerald-500" data-testid="crew-pending-earnings">{fmtMoney(earnings.pending)}</p>
                  <p className="text-[11px] text-muted-foreground">Next payout {fmtDate(earnings.next_payout)}</p>
                </div>
              )}
            </div>

            {/* WORK */}
            {section === "work" && (() => {
              const backlog = data.all_active.filter((m) =>
                !data.todays.some((t) => t.id === m.id) && !data.upcoming.some((u) => u.id === m.id));
              return (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-xl border bg-card p-5 card-lift"><Rocket className="w-4 h-4 text-[var(--cc-accent)]" /><p className="text-3xl font-heading font-bold mt-2" data-testid="crew-kpi-today">{data.todays.length}</p><p className="text-xs text-muted-foreground mt-1">Today</p></div>
                    <div className="rounded-xl border bg-card p-5 card-lift"><CalendarDays className="w-4 h-4 text-muted-foreground" /><p className="text-3xl font-heading font-bold mt-2" data-testid="crew-kpi-upcoming">{data.upcoming.length}</p><p className="text-xs text-muted-foreground mt-1">Upcoming</p></div>
                    <div className="rounded-xl border bg-card p-5 card-lift"><Trophy className="w-4 h-4 text-muted-foreground" /><p className="text-3xl font-heading font-bold mt-2" data-testid="crew-kpi-completed">{data.completed_count}</p><p className="text-xs text-muted-foreground mt-1">Completed</p></div>
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-lg mb-4">Today's Missions</h2>
                    <div className="space-y-3">
                      {data.todays.length === 0 && <p className="text-sm text-muted-foreground rounded-xl border border-dashed p-6 text-center">Nothing on the board today.</p>}
                      {data.todays.map((m) => <MissionCard key={m.id} m={m} />)}
                    </div>
                  </div>
                  {backlog.length > 0 && (
                    <div data-testid="crew-backlog-section">
                      <h2 className="font-heading font-semibold text-lg mb-4">Needs Attention</h2>
                      <div className="space-y-3">{backlog.map((m) => <MissionCard key={m.id} m={m} />)}</div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* SCHEDULE (Available / Taken) */}
            {section === "schedule" && (
              <div data-testid="crew-schedule">
                <div className="flex gap-2 rounded-full border p-1 w-fit mb-6">
                  {[["available", "Available"], ["taken", "Taken"]].map(([id, label]) => (
                    <button key={id} data-testid={`crew-schedule-${id}`} onClick={() => setScheduleView(id)}
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${scheduleView === id ? "bg-[var(--cc-accent)] text-black" : "text-muted-foreground hover:text-foreground"}`}>
                      {label}{id === "available" && openBoard.length > 0 ? ` · ${openBoard.length}` : ""}
                    </button>
                  ))}
                </div>

                {scheduleView === "available" ? (
                  <div data-testid="crew-job-board">
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground">Claim a job — payout hits your earnings when it's complete.</p>
                      {pushState === "enabled" && (
                        <button onClick={fireTest} data-testid="crew-push-test" className="text-[var(--cc-accent)] text-xs font-semibold hover:opacity-80">Send test alert</button>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {board.map((m) => <BoardCard key={m.id} m={m} />)}
                      {board.length === 0 && (
                        <p className="text-sm text-muted-foreground rounded-xl border border-dashed p-10 text-center col-span-full">No open jobs right now. New postings will notify you instantly.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div data-testid="crew-taken-jobs" className="space-y-3">
                    {data.upcoming.length === 0 && <p className="text-sm text-muted-foreground rounded-xl border border-dashed p-10 text-center">No upcoming jobs on your schedule yet. Claim one from Available.</p>}
                    {data.upcoming.map((m) => <MissionCard key={m.id} m={m} />)}
                  </div>
                )}
              </div>
            )}

            {/* EARNINGS */}
            {section === "earnings" && earnings && (
              <div className="space-y-6" data-testid="crew-earnings">
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-xl border bg-card p-5"><p className="overline-cc">Pending</p><p className="text-2xl font-heading font-bold mt-1.5 text-emerald-500" data-testid="earnings-pending">{fmtMoney(earnings.pending)}</p></div>
                  <div className="rounded-xl border bg-card p-5"><p className="overline-cc">Next Payout</p><p className="text-2xl font-heading font-bold mt-1.5" data-testid="earnings-next-payout">{fmtDate(earnings.next_payout)}</p><p className="text-[10px] text-muted-foreground mt-0.5">Auto every Tue & Fri</p></div>
                  <div className="rounded-xl border bg-card p-5"><p className="overline-cc">Total Paid</p><p className="text-2xl font-heading font-bold mt-1.5" data-testid="earnings-paid-total">{fmtMoney(earnings.paid_total)}</p></div>
                </div>
                {earnings.referral_code && (
                  <div className="rounded-xl border bg-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="crew-referral-card">
                    <div>
                      <p className="overline-cc">Your Referral Code</p>
                      <p className="text-xs text-muted-foreground mt-1">New clients get 10% off their first booking with your code.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="rounded-lg border bg-secondary/50 px-3 py-2 font-mono-cc text-sm font-bold tracking-wider" data-testid="crew-referral-code">{earnings.referral_code}</code>
                      <Button size="icon" variant="outline" className="h-9 w-9" data-testid="crew-referral-copy" aria-label="Copy referral code"
                        onClick={() => { navigator.clipboard.writeText(earnings.referral_code); toast.success("Code copied"); }}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="rounded-xl border bg-card">
                  <div className="px-5 py-3.5 border-b"><h2 className="font-heading font-semibold text-sm">Job Earnings</h2></div>
                  <div className="divide-y max-h-72 overflow-y-auto">
                    {earnings.records.map((r) => (
                      <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`earning-${r.id}`}>
                        <div className="min-w-0"><p className="text-sm font-semibold font-mono-cc">{r.mission_number}</p><p className="text-[11px] text-muted-foreground">{fmtDate(r.created_at)}</p></div>
                        <div className="flex items-center gap-3"><span className="font-mono-cc text-sm font-semibold">{fmtMoney(r.amount)}</span><StatusBadge status={r.status} /></div>
                      </div>
                    ))}
                    {earnings.records.length === 0 && <p className="px-5 py-8 text-xs text-muted-foreground text-center">Complete jobs to start earning. Claim one from Schedule.</p>}
                  </div>
                </div>
                <div className="rounded-xl border bg-card">
                  <div className="px-5 py-3.5 border-b"><h2 className="font-heading font-semibold text-sm">Payment History</h2></div>
                  <div className="divide-y max-h-64 overflow-y-auto">
                    {earnings.payments.map((p) => (
                      <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`payment-${p.id}`}>
                        <div className="min-w-0"><p className="text-sm font-semibold">{fmtMoney(p.amount)}</p><p className="text-[11px] text-muted-foreground truncate">{p.note || "Payout"} · {fmtDate(p.created_at)}</p></div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      </div>
                    ))}
                    {earnings.payments.length === 0 && <p className="px-5 py-8 text-xs text-muted-foreground text-center">No payouts yet.</p>}
                  </div>
                </div>
              </div>
            )}

            {/* EXPENSES (coming soon) */}
            {section === "expenses" && (
              <div className="rounded-2xl border border-dashed bg-card p-12 text-center" data-testid="crew-expenses">
                <Receipt className="w-12 h-12 text-[var(--cc-accent)] mx-auto" />
                <h2 className="font-heading font-bold text-xl mt-4">Expenses are coming soon</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">Soon you'll log gas, tolls, and supplies right here and get reimbursed with your payout.</p>
              </div>
            )}

            {/* ACCOUNT */}
            {section === "account" && data && (
              <div className="space-y-5" data-testid="crew-account">
                <div className="rounded-xl border bg-card p-5 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[var(--cc-accent)]/15 border border-[var(--cc-accent)]/40 flex items-center justify-center font-heading font-extrabold text-xl text-[var(--cc-accent)]">
                    {(data.employee.name || "C").slice(0, 1)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-lg truncate">{data.employee.name}</p>
                    <div className="flex items-center gap-2 mt-1"><LevelBadge level={data.employee.crew_level} /><span className="text-xs text-muted-foreground">{data.employee.role}</span></div>
                  </div>
                </div>

                <div className="rounded-xl border bg-card p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div><p className="font-heading font-semibold text-sm">Availability</p><p className="text-xs text-muted-foreground">Set whether you're open for jobs.</p></div>
                    <Select value={data.employee.status} onValueChange={setStatus}>
                      <SelectTrigger className="w-40 h-10 rounded-full text-sm" data-testid="crew-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="on_mission">On Mission</SelectItem>
                        <SelectItem value="off_duty">Off Duty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t pt-4">
                    <div><p className="font-heading font-semibold text-sm">Live location sharing</p><p className="text-xs text-muted-foreground">Lets dispatch and customers see your GPS on active jobs.</p></div>
                    <button data-testid="crew-gps-toggle" onClick={() => setSharing(!sharing)}
                      className={`px-4 py-2 rounded-full border text-sm font-semibold flex items-center gap-2 transition-colors ${sharing ? "border-emerald-500/60 text-emerald-500 bg-emerald-500/10" : "text-muted-foreground"}`}>
                      <Navigation className="w-4 h-4" /> {sharing ? "On" : "Off"}
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t pt-4">
                    <div><p className="font-heading font-semibold text-sm">Job alerts</p><p className="text-xs text-muted-foreground">Get a push the moment new jobs post.</p></div>
                    <div className="flex items-center gap-2">
                      {pushState === "enabled" && <button onClick={fireTest} data-testid="crew-push-test" className="text-[var(--cc-accent)] text-xs font-semibold">Test</button>}
                      <button data-testid="crew-push-toggle" onClick={togglePush} disabled={pushBusy}
                        className={`px-4 py-2 rounded-full border text-sm font-semibold flex items-center gap-2 transition-colors ${pushState === "enabled" ? "border-emerald-500/60 text-emerald-500 bg-emerald-500/10" : "text-muted-foreground"}`}>
                        {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />} {pushState === "enabled" ? "On" : "Off"}
                      </button>
                    </div>
                  </div>
                </div>

                <Button variant="outline" className="w-full rounded-full h-12" data-testid="crew-account-logout" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </Button>
              </div>
            )}

            {/* HELP CENTER */}
            {section === "help" && (
              <div className="space-y-4" data-testid="crew-help">
                <div className="rounded-xl border bg-card p-6">
                  <h2 className="font-heading font-bold text-lg">Need a hand?</h2>
                  <p className="text-sm text-muted-foreground mt-1.5">Dispatch is on standby for anything on the road — running late, access issues, or a tricky item.</p>
                  <div className="mt-5 space-y-3">
                    <a href="tel:+16195550100" data-testid="crew-help-call" className="flex items-center gap-3 rounded-xl border p-4 hover:border-[var(--cc-accent)] transition-colors">
                      <Phone className="w-5 h-5 text-[var(--cc-accent)]" /><div><p className="font-semibold text-sm">Call dispatch</p><p className="text-xs text-muted-foreground">(619) 555-0100 · 7am–9pm</p></div>
                    </a>
                    <a href="mailto:contact@castle-corp.com" data-testid="crew-help-email" className="flex items-center gap-3 rounded-xl border p-4 hover:border-[var(--cc-accent)] transition-colors">
                      <Mail className="w-5 h-5 text-[var(--cc-accent)]" /><div><p className="font-semibold text-sm">Email support</p><p className="text-xs text-muted-foreground">contact@castle-corp.com</p></div>
                    </a>
                  </div>
                </div>
                <div className="rounded-xl border bg-card p-6">
                  <h3 className="font-heading font-semibold">Quick tips</h3>
                  <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc pl-5">
                    <li>Slide to start once you arrive at the pickup — customers get notified live.</li>
                    <li>Add before/after photos on the job to protect yourself and speed up sign-off.</li>
                    <li>Keep location sharing on so dispatch can route you the fastest way.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
        {liveJob && (
          <LiveJobScreen job={liveJob} photos={photosMap[liveJob.id] || []} uploading={uploading} me={myPos}
            onClose={() => setLiveJob(null)} onPatch={patchLive} onAddPhoto={addPhoto} />
        )}
      </main>
    </div>
  );
}
